"use client";

import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { PWA_INSTALL_SNOOZE_KEY, PWA_INSTALLED_KEY } from "@/constants";
import { getParsedLocalStorageItem, setLocalStorageItem, useLocalStorage } from "@/hooks/storage";
import {
  type DevicePlatform,
  getDevicePlatform,
  getPwaDisplayMode,
  isInAppBrowser,
  isIosSafari,
  type PwaDisplayMode,
} from "@/utils/device";
import { Logger } from "@/utils/logger";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface DismissResult {
  snoozeCount: number;
  permanent: boolean;
}

interface PwaInstallContextValue {
  platform: DevicePlatform;
  displayMode: PwaDisplayMode;
  canInstallNatively: boolean;
  installed: boolean;
  iosSafari: boolean;
  inAppBrowser: boolean;
  promptInstall: () => Promise<"accepted" | "dismissed" | null>;
  shouldShow: boolean;
  dismiss: () => DismissResult;
  nativeDecline: () => void;
}

const PwaInstallContext = createContext<PwaInstallContextValue | null>(null);

export function usePwaInstall() {
  const ctx = useContext(PwaInstallContext);
  if (!ctx) throw new Error("usePwaInstall must be used within a PwaInstallProvider");
  return ctx;
}

interface EnvState {
  platform: DevicePlatform;
  displayMode: PwaDisplayMode;
  iosSafari: boolean;
  inAppBrowser: boolean;
  installed: boolean;
}

const SSR_ENV_STATE: EnvState = {
  platform: "other",
  displayMode: "browser",
  iosSafari: false,
  inAppBrowser: false,
  installed: false,
};

const getServerEnv = () => SSR_ENV_STATE;
const subscribeNoop = () => () => undefined;
const getHydratedClient = () => true;
const getHydratedServer = () => false;

interface EnvStore {
  subscribe: (onChange: () => void) => () => void;
  getSnapshot: () => EnvState;
  markInstalled: () => void;
}

// Per provider instance, so a remount re-reads the browser.
function createEnvStore(): EnvStore {
  let snapshot: EnvState | undefined;
  const listeners = new Set<() => void>();
  const getSnapshot = (): EnvState => {
    if (!snapshot) {
      const displayMode = getPwaDisplayMode();
      snapshot = {
        platform: getDevicePlatform(),
        displayMode,
        iosSafari: isIosSafari(),
        inAppBrowser: isInAppBrowser(),
        installed: !!getParsedLocalStorageItem(PWA_INSTALLED_KEY) || displayMode === "standalone",
      };
    }
    return snapshot;
  };
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    getSnapshot,
    markInstalled() {
      const current = getSnapshot();
      if (current.installed) return;
      snapshot = { ...current, installed: true };
      listeners.forEach((listener) => listener());
    },
  };
}

// 90 days matches Chrome's own cooldown after a user dismisses the native
// install dialog — re-appearing sooner would show a degraded manual-steps
// fallback instead of the native prompt.
const SNOOZE_DAYS = 90;
const MAX_SNOOZE_COUNT = 3;

interface SnoozeState {
  count: number;
  until: string | null;
}

const DEFAULT_SNOOZE_STATE: SnoozeState = { count: 0, until: null };

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function isSnoozed(snoozedUntil: string | null): boolean {
  if (!snoozedUntil) return false;
  const until = new Date(snoozedUntil).getTime();
  if (!Number.isFinite(until)) return false;
  return Date.now() < until;
}

export function PwaInstallProvider({ children }: PropsWithChildren) {
  // Reading browser APIs in a useState initializer would mismatch hydration.
  const [envStore] = useState(createEnvStore);
  const env = useSyncExternalStore(envStore.subscribe, envStore.getSnapshot, getServerEnv);
  const hydrated = useSyncExternalStore(subscribeNoop, getHydratedClient, getHydratedServer);
  const [canInstallNatively, setCanInstallNatively] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  // Chrome throws InvalidStateError if prompt() is called while a prior
  // userChoice promise is still unresolved. Guard against double-clicks.
  const promptPending = useRef(false);

  const [snoozeState, setSnoozeState, snoozeLoading] = useLocalStorage<SnoozeState>(
    PWA_INSTALL_SNOOZE_KEY,
    DEFAULT_SNOOZE_STATE,
  );
  const markInstalled = useCallback(() => {
    setLocalStorageItem(PWA_INSTALLED_KEY, true);
    envStore.markInstalled();
  }, [envStore]);

  useEffect(() => {
    if (getPwaDisplayMode() === "standalone" && !getParsedLocalStorageItem(PWA_INSTALLED_KEY)) {
      setLocalStorageItem(PWA_INSTALLED_KEY, true);
    }
  }, []);

  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      // Suppress the native mini-infobar on Android so we can show our own CTA.
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanInstallNatively(true);
    };

    const onAppInstalled = () => {
      deferredPrompt.current = null;
      setCanInstallNatively(false);
      markInstalled();
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, [markInstalled]);

  const promptInstall = useCallback(async (): Promise<"accepted" | "dismissed" | null> => {
    if (!deferredPrompt.current || promptPending.current) return null;
    promptPending.current = true;
    try {
      await deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
      setCanInstallNatively(false);
      if (outcome === "accepted") markInstalled();
      return outcome;
    } catch (error) {
      // Chrome can throw InvalidStateError if the deferred event has already
      // been consumed or the user navigated mid-prompt. Treat as "no native
      // prompt available" so the caller can fall through to its no-prompt
      // branch (which closes the modal) instead of bubbling an unhandled
      // rejection that leaves the modal stuck open.
      Logger.error(error, { message: "PWA install prompt failed" });
      deferredPrompt.current = null;
      setCanInstallNatively(false);
      return null;
    } finally {
      promptPending.current = false;
    }
  }, [markInstalled]);

  // Read snoozeState through a ref so the callbacks can both return the next
  // values synchronously and stay race-safe against two rapid clicks in the
  // same React tick. The optimistic write to `snoozeStateRef.current` after
  // computing `next` means a second click in that tick reads post-first
  // state, not stale render state.
  const snoozeStateRef = useRef(snoozeState);
  useEffect(() => {
    snoozeStateRef.current = snoozeState;
  }, [snoozeState]);

  const dismiss = useCallback((): DismissResult => {
    const prev = snoozeStateRef.current;
    // Once permanently dismissed, never resurrect — even if a path like
    // the `?modal=pwa-install` deep-link re-opens the modal. Without this
    // guard, dismiss would rewrite `{count: MAX, until: null}` back down
    // to `{count: MAX, until: +7d}`, un-doing the "don't show again" decision.
    if (prev.count >= MAX_SNOOZE_COUNT) return { snoozeCount: prev.count, permanent: true };
    const nextCount = prev.count + 1;
    const permanent = nextCount >= MAX_SNOOZE_COUNT;
    const next: SnoozeState = {
      count: nextCount,
      until: permanent ? null : addDays(new Date(), SNOOZE_DAYS).toISOString(),
    };
    snoozeStateRef.current = next;
    setSnoozeState(next);
    return { snoozeCount: nextCount, permanent };
  }, [setSnoozeState]);

  // Cap below MAX_SNOOZE_COUNT so a user who declines Chrome's native prompt
  // can still recover the flow (e.g., via the desktop QR or a fresh visit
  // after the 30-day window). Letting the native-decline count hit MAX would
  // hard-block the funnel for users who tapped "Install" on the banner but
  // mis-dismissed Chrome's confirm — a likely-accidental gesture.
  const nativeDecline = useCallback(() => {
    const prev = snoozeStateRef.current;
    const nextCount = Math.min(prev.count + 1, MAX_SNOOZE_COUNT - 1);
    if (nextCount === prev.count) return;
    const next: SnoozeState = {
      count: nextCount,
      until: addDays(new Date(), SNOOZE_DAYS).toISOString(),
    };
    snoozeStateRef.current = next;
    setSnoozeState(next);
  }, [setSnoozeState]);

  const shouldShow =
    hydrated &&
    !snoozeLoading &&
    env.displayMode === "browser" &&
    !env.installed &&
    !env.inAppBrowser &&
    (env.platform !== "ios" || env.iosSafari) &&
    snoozeState.count < MAX_SNOOZE_COUNT &&
    !isSnoozed(snoozeState.until);

  const value = useMemo<PwaInstallContextValue>(
    () => ({ ...env, canInstallNatively, promptInstall, shouldShow, dismiss, nativeDecline }),
    [env, canInstallNatively, promptInstall, shouldShow, dismiss, nativeDecline],
  );

  return <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>;
}
